import { type QueryClient, useQuery } from '@tanstack/react-query';
import { checkCache, storeInCache } from '@vakantiesnl/components/src/utils/redis';
import { ContentfulClient } from '@vakantiesnl/services/src/contentful/ContentfulClient';
import { getDaysInSec, getMinutesInMs, errorHandler, parseErrorToReport } from '@vakantiesnl/services/src/util';
import { type ImageAsset } from '@vakantiesnl/types/src/contentful';
import { gql } from 'graphql-request';

import { FRAGMENT_IMAGE_FIELDS } from './fragments';
import { replaceUndefinedByNull } from '../../util/replaceUndefinedByNull';

const TOP_BANNER_CACHE_KEY = 'contentful-acco-banner';

export function useGetAccommodationTopBannerData(entityId?: string): {
	accommodationTopBannerData: AccommodationTopBannerItem | null;
	isLoadingCountryData: boolean;
} {
	const query = useQuery({
		queryKey: [TOP_BANNER_CACHE_KEY, entityId],
		queryFn: () => getAccommodationTopBannerData(entityId).then(replaceUndefinedByNull),
		enabled: !!entityId,
		staleTime: getMinutesInMs(15),
	});

	return {
		accommodationTopBannerData: query.data || null,
		isLoadingCountryData: query.isLoading,
	};
}

/* eslint-disable-next-line @typescript-eslint/explicit-function-return-type */
export async function prefetchAccommodationTopBannerData(queryClient: QueryClient, entityId: string) {
	try {
		await queryClient.prefetchQuery({
			queryKey: [TOP_BANNER_CACHE_KEY, entityId],
			queryFn: () => getAccommodationTopBannerData(entityId).then(replaceUndefinedByNull),
		});
	} catch (e) {
		errorHandler.report(
			parseErrorToReport(e, `Error prefetching accommodation top banner data with entityId: ${entityId}`),
		);
	}
}

export const getAccommodationTopBannerData = async (entityId?: string): Promise<AccommodationTopBannerItem | null> => {
	if (!entityId) return null;

	try {
		const data = await checkCache<AccommodationTopBannerItem>(`${TOP_BANNER_CACHE_KEY}:${entityId}`);

		if (data) {
			/** Only return non-empty cached results */
			if (Object.keys(data).length > 0) return data;

			return null;
		}

		const rawAccommodationTopBannerResponse = await ContentfulClient.request<
			RawAccommodationTopBannerResponse,
			{ entityId: string }
		>({
			signal: AbortSignal.timeout(20000),
			document: accommodationTopBannerQuery,
			variables: { entityId },
			requestHeaders: { Authorization: `Bearer ${process.env.NEXT_PUBLIC_CONTENTFUL_API_KEY}` },
		});

		if (rawAccommodationTopBannerResponse?.accommodationBannerCollection?.items[0]) {
			const topBannerData = rawAccommodationTopBannerResponse.accommodationBannerCollection.items[0];

			await storeInCache(`${TOP_BANNER_CACHE_KEY}:${entityId}`, topBannerData);

			return topBannerData;
		}

		/** Cache an empty result so it's stored in redis for 1 day */
		await storeInCache(`${TOP_BANNER_CACHE_KEY}:${entityId}`, {}, getDaysInSec(1));
		return null;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(`Error fetching topBanner data for entityId: ${entityId}`, error);
		return null;
	}
};

export type AccommodationTopBannerItem = {
	entityId: string;
	largeBanner: ImageAsset;
	smallBanner: ImageAsset;
	url: string | null;
};

export type RawAccommodationTopBannerResponse = {
	accommodationBannerCollection: {
		items: AccommodationTopBannerItem[] | never[];
	};
};

export const accommodationTopBannerQuery = gql`
	query AccommodationTopHeaderImage($entityId: String!) {
		accommodationBannerCollection(limit: 1, where: { entityId: $entityId }) {
			items {
				entityId
				largeBanner {
					...imageAsset
				}
				smallBanner {
					...imageAsset
				}
				url
			}
		}
	}

	${FRAGMENT_IMAGE_FIELDS}
`;
